import { useState, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Download, Upload, Trash2, AlertTriangle, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog } from '@/components/ui/dialog';
import { PageBody, PageHeader } from '@/components/AppShell';
import { api, ApiError } from '@/lib/api';

interface ImportSummary {
  users: number;
  accounts: number;
  chartOfAccounts: number;
  costCenters: number;
  parties: number;
  transactions: number;
  payables: number;
  receivables: number;
  recurringRules: number;
  auditLogs: number;
}

interface ExportPayload {
  format: string;
  version: number;
  exportedAt: string;
  exportedBy: { id: string; email: string };
  organizationId: string;
  data: Record<string, unknown>;
}

function downloadJson(payload: unknown, filename: string) {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Falha ao ler o arquivo'));
    reader.onload = () => resolve(String(reader.result));
    reader.readAsText(file);
  });
}

export function ManutencaoPage() {
  const qc = useQueryClient();

  // -------- EXPORT --------
  const exportMut = useMutation({
    mutationFn: () => api<ExportPayload>('/maintenance/export'),
    onSuccess: (payload) => {
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      downloadJson(payload, `rad-financeiro-export-${stamp}.json`);
    },
  });

  // -------- IMPORT --------
  const fileRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importConfirm, setImportConfirm] = useState('');
  const [importResult, setImportResult] = useState<ImportSummary | null>(null);

  const importMut = useMutation({
    mutationFn: async (file: File) => {
      const text = await readFileAsText(file);
      let json: unknown;
      try {
        json = JSON.parse(text);
      } catch {
        throw new Error('Arquivo não é um JSON válido.');
      }
      return api<{ ok: true; imported: ImportSummary }>('/maintenance/import', {
        method: 'POST',
        body: json,
      });
    },
    onSuccess: (res) => {
      setImportResult(res.imported);
      setImportOpen(false);
      setImportConfirm('');
      setSelectedFile(null);
      if (fileRef.current) fileRef.current.value = '';
      qc.invalidateQueries();
    },
  });

  // -------- CLEAR --------
  const [clearOpen, setClearOpen] = useState(false);
  const [clearConfirm, setClearConfirm] = useState('');
  const [clearDone, setClearDone] = useState(false);

  const clearMut = useMutation({
    mutationFn: () => api<{ ok: true }>('/maintenance/clear', { method: 'POST' }),
    onSuccess: () => {
      setClearOpen(false);
      setClearConfirm('');
      setClearDone(true);
      qc.invalidateQueries();
    },
  });

  // -------- CLEAR AUDIT LOGS --------
  const [clearAuditOpen, setClearAuditOpen] = useState(false);
  const [clearAuditPassword, setClearAuditPassword] = useState('');
  const [clearAuditDeleted, setClearAuditDeleted] = useState<number | null>(null);

  const clearAuditMut = useMutation({
    mutationFn: (password: string) =>
      api<{ ok: true; deleted: number }>('/maintenance/clear-audit-logs', {
        method: 'POST',
        body: { password },
      }),
    onSuccess: (res) => {
      setClearAuditOpen(false);
      setClearAuditPassword('');
      setClearAuditDeleted(res.deleted);
      qc.invalidateQueries();
    },
  });

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null;
    setSelectedFile(f);
    setImportResult(null);
  }

  function startImport() {
    if (!selectedFile) return;
    setImportConfirm('');
    setImportOpen(true);
  }

  function confirmImport() {
    if (selectedFile && importConfirm.trim().toUpperCase() === 'CONFIRMAR') {
      importMut.mutate(selectedFile);
    }
  }

  function startClear() {
    setClearConfirm('');
    setClearDone(false);
    setClearOpen(true);
  }

  function confirmClear() {
    if (clearConfirm.trim().toUpperCase() === 'LIMPAR') {
      clearMut.mutate();
    }
  }

  function startClearAudit() {
    setClearAuditPassword('');
    setClearAuditDeleted(null);
    clearAuditMut.reset();
    setClearAuditOpen(true);
  }

  function confirmClearAudit() {
    if (clearAuditPassword.length > 0) {
      clearAuditMut.mutate(clearAuditPassword);
    }
  }

  return (
    <>
      <PageHeader title="Manutenção" breadcrumb="Configurações" />
      <PageBody>
        <div className="rounded-md border border-amber-300 bg-amber-50 text-amber-900 p-4 flex gap-3">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <div className="text-sm leading-relaxed">
            Esta tela executa operações destrutivas sobre toda a base.{' '}
            <strong>Importar</strong> e <strong>Limpar</strong> apagam definitivamente os
            dados da organização atual (preservando apenas o seu usuário administrador).{' '}
            <strong>Faça um Exportar antes</strong> de qualquer das duas operações.
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download size={18} /> Exportar base
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Baixa um arquivo JSON com todas as tabelas da organização (usuários, contas,
              plano de contas, contrapartes, lançamentos, contas a pagar/receber,
              recorrentes e logs de auditoria). Use este arquivo para migrar a aplicação
              de DEV para PROD ou como backup pontual.
            </p>
            <Button
              onClick={() => exportMut.mutate()}
              disabled={exportMut.isPending}
            >
              {exportMut.isPending ? 'Gerando…' : 'Exportar agora'}
            </Button>
            {exportMut.error instanceof ApiError && (
              <p className="text-sm text-destructive">Erro: {exportMut.error.message}</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload size={18} /> Importar base
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Selecione um arquivo gerado pelo Exportar acima.{' '}
              <strong>A base atual será apagada</strong> e substituída pelo conteúdo do
              arquivo. Seu próprio usuário administrador e a organização são mantidos.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
              <div className="flex-1 space-y-1.5">
                <Label htmlFor="import-file">Arquivo .json</Label>
                <Input
                  id="import-file"
                  ref={fileRef}
                  type="file"
                  accept="application/json,.json"
                  onChange={onPickFile}
                />
              </div>
              <Button
                variant="destructive"
                onClick={startImport}
                disabled={!selectedFile || importMut.isPending}
              >
                {importMut.isPending ? 'Importando…' : 'Importar'}
              </Button>
            </div>
            {importMut.error instanceof Error && (
              <p className="text-sm text-destructive">Erro: {importMut.error.message}</p>
            )}
            {importResult && (
              <div className="text-sm bg-muted/50 rounded-md p-3 space-y-1">
                <div className="font-medium text-foreground">Import concluído</div>
                <ul className="grid grid-cols-2 gap-x-6 gap-y-0.5 text-muted-foreground">
                  <li>Usuários: {importResult.users}</li>
                  <li>Contas: {importResult.accounts}</li>
                  <li>Plano de contas: {importResult.chartOfAccounts}</li>
                  <li>Centros de custo: {importResult.costCenters}</li>
                  <li>Contrapartes: {importResult.parties}</li>
                  <li>Lançamentos: {importResult.transactions}</li>
                  <li>Contas a pagar: {importResult.payables}</li>
                  <li>Contas a receber: {importResult.receivables}</li>
                  <li>Recorrentes: {importResult.recurringRules}</li>
                  <li>Logs de auditoria: {importResult.auditLogs}</li>
                </ul>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-destructive/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <FileText size={18} /> Limpar log de auditoria
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Apaga <strong>todos os registros de auditoria</strong> da organização atual
              (logins, alterações de cadastro, exports, imports, limpezas etc.). Os demais
              dados — lançamentos, contas, contrapartes, plano de contas — permanecem
              intactos. Esta ação não pode ser desfeita; informe a senha do seu usuário
              administrador (a mesma usada no login, originalmente definida em{' '}
              <code className="font-mono text-xs">ADMIN_INITIAL_PASSWORD</code>) para
              confirmar.
            </p>
            <Button
              variant="destructive"
              onClick={startClearAudit}
              disabled={clearAuditMut.isPending}
            >
              {clearAuditMut.isPending ? 'Limpando…' : 'Limpar log de auditoria'}
            </Button>
            {clearAuditDeleted !== null && (
              <p className="text-sm text-emerald-700">
                {clearAuditDeleted === 0
                  ? 'Nenhum registro a apagar — log de auditoria já estava vazio.'
                  : clearAuditDeleted === 1
                    ? '1 registro de auditoria apagado.'
                    : `${clearAuditDeleted} registros de auditoria apagados.`}
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="border-destructive/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <Trash2 size={18} /> Limpar aplicação
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Apaga todos os lançamentos, cadastros, contas, usuários (exceto o seu) e logs
              da organização atual. A organização em si é mantida. Esta ação não pode ser
              desfeita.
            </p>
            <Button variant="destructive" onClick={startClear} disabled={clearMut.isPending}>
              {clearMut.isPending ? 'Limpando…' : 'Limpar tudo'}
            </Button>
            {clearMut.error instanceof ApiError && (
              <p className="text-sm text-destructive">Erro: {clearMut.error.message}</p>
            )}
            {clearDone && (
              <p className="text-sm text-emerald-700">
                Base zerada. Você pode começar do zero.
              </p>
            )}
          </CardContent>
        </Card>
      </PageBody>

      {/* Modal de confirmacao do Import */}
      <Dialog open={importOpen} onClose={() => setImportOpen(false)} title="Confirmar import">
        <div className="px-6 py-4 space-y-3 text-sm">
          <p>
            Você está prestes a <strong>apagar a base atual</strong> e substituir pelo
            conteúdo de:
          </p>
          <p className="font-mono text-xs bg-muted p-2 rounded break-all">
            {selectedFile?.name}
          </p>
          <p>
            Para confirmar, digite <strong>CONFIRMAR</strong> abaixo:
          </p>
          <Input
            autoFocus
            value={importConfirm}
            onChange={(e) => setImportConfirm(e.target.value)}
            placeholder="CONFIRMAR"
          />
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-2">
          <Button variant="outline" onClick={() => setImportOpen(false)}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={confirmImport}
            disabled={
              importConfirm.trim().toUpperCase() !== 'CONFIRMAR' || importMut.isPending
            }
          >
            {importMut.isPending ? 'Importando…' : 'Apagar e importar'}
          </Button>
        </div>
      </Dialog>

      {/* Modal de confirmacao do Limpar Log de Auditoria */}
      <Dialog
        open={clearAuditOpen}
        onClose={() => setClearAuditOpen(false)}
        title="Confirmar limpeza do log de auditoria"
      >
        <div className="px-6 py-4 space-y-3 text-sm">
          <p>
            Esta ação apaga <strong>todos os registros de auditoria</strong> da
            organização atual. Os demais dados (lançamentos, cadastros, contas) são
            preservados.
          </p>
          <p>
            Para confirmar, informe a senha do seu usuário administrador
            (originalmente definida em{' '}
            <code className="font-mono text-xs">ADMIN_INITIAL_PASSWORD</code>):
          </p>
          <div className="space-y-1.5">
            <Label htmlFor="clear-audit-password">Senha do administrador</Label>
            <Input
              id="clear-audit-password"
              autoFocus
              type="password"
              autoComplete="current-password"
              value={clearAuditPassword}
              onChange={(e) => setClearAuditPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && clearAuditPassword.length > 0 && !clearAuditMut.isPending) {
                  confirmClearAudit();
                }
              }}
            />
          </div>
          {clearAuditMut.error instanceof ApiError && (
            <p className="text-sm text-destructive">
              {clearAuditMut.error.status === 403
                ? 'Senha incorreta.'
                : `Erro: ${clearAuditMut.error.message}`}
            </p>
          )}
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-2">
          <Button variant="outline" onClick={() => setClearAuditOpen(false)}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={confirmClearAudit}
            disabled={clearAuditPassword.length === 0 || clearAuditMut.isPending}
          >
            {clearAuditMut.isPending ? 'Limpando…' : 'Apagar log de auditoria'}
          </Button>
        </div>
      </Dialog>

      {/* Modal de confirmacao do Limpar */}
      <Dialog open={clearOpen} onClose={() => setClearOpen(false)} title="Confirmar limpeza">
        <div className="px-6 py-4 space-y-3 text-sm">
          <p>
            Esta ação apaga <strong>todos os dados</strong> da organização (lançamentos,
            cadastros, contas, outros usuários e logs). Será mantido apenas o seu usuário
            administrador.
          </p>
          <p>
            Para confirmar, digite <strong>LIMPAR</strong> abaixo:
          </p>
          <Input
            autoFocus
            value={clearConfirm}
            onChange={(e) => setClearConfirm(e.target.value)}
            placeholder="LIMPAR"
          />
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-2">
          <Button variant="outline" onClick={() => setClearOpen(false)}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={confirmClear}
            disabled={clearConfirm.trim().toUpperCase() !== 'LIMPAR' || clearMut.isPending}
          >
            {clearMut.isPending ? 'Limpando…' : 'Apagar tudo'}
          </Button>
        </div>
      </Dialog>
    </>
  );
}
